import {MilestoneFragment, LazyMilestonePicker, MilestonePickerBase} from '@github-ui/item-picker/MilestonePicker'
import type {
  MilestonePickerMilestone$key,
  MilestonePickerMilestone$data,
} from '@github-ui/item-picker/MilestonePickerMilestone.graphql'
import {ActionList, type SxProp} from '@primer/react'
import {useMemo, Suspense, useState, type HTMLAttributes} from 'react'
import {graphql, type PreloadedQuery, readInlineData, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import type {MilestonesSectionFragment$key} from './__generated__/MilestonesSectionFragment.graphql'
import {Milestone} from './Milestone'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {HOTKEYS} from '@github-ui/item-picker/Hotkeys'
import {useKeyPress} from '@github-ui/use-key-press'
import type {MilestonePickerQuery} from '@github-ui/item-picker/MilestonePickerQuery.graphql'
import type {MilestonesSectionMilestone$key} from './__generated__/MilestonesSectionMilestone.graphql'

const ReadonlyMilestonesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />
const MilestonesSectionHeader = (props: HTMLAttributes<HTMLButtonElement>) => (
  <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={props} />
)

type MilestonesSectionProps = {
  sectionHeader: JSX.Element
  milestone: MilestonePickerMilestone$data | null
} & SxProp
const MilestonesSection = ({sectionHeader, milestone, sx}: MilestonesSectionProps) => (
  <Section sx={sx} sectionHeader={sectionHeader} emptyText={milestone ? undefined : LABELS.emptySections.milestones}>
    <ActionList variant="full">
      <Milestone milestone={milestone} />
    </ActionList>
  </Section>
)

export type CreateIssueMilestonesSectionProps = {
  activeMilestone: MilestonePickerMilestone$data | null
  milestonesRef: PreloadedQuery<MilestonePickerQuery, Record<string, unknown>>
  onSelectionChange: (milestones: MilestonePickerMilestone$data[]) => void
  viewerCanSetMilestone: boolean
  insidePortal: boolean
  shortcutEnabled: boolean
  shortcutHintVisible: boolean
} & SxProp
export function CreateIssueMilestonesSection({
  activeMilestone,
  milestonesRef,
  onSelectionChange,
  viewerCanSetMilestone,
  sx,
  ...sharedConfigProps
}: CreateIssueMilestonesSectionProps) {
  const [milestone, setMilestone] = useState<MilestonePickerMilestone$data | null>(activeMilestone)

  const sectionHeader = !viewerCanSetMilestone ? (
    <ReadonlyMilestonesSectionHeader />
  ) : (
    <MilestonePickerBase
      activeMilestone={milestone}
      milestoneQueryRef={milestonesRef}
      onSelectionChange={milestones => {
        setMilestone(milestones[0] ?? null)
        onSelectionChange(milestones)
      }}
      anchorElement={MilestonesSectionHeader}
      {...sharedConfigProps}
    />
  )

  return <MilestonesSection sx={sx} sectionHeader={sectionHeader} milestone={milestone} />
}

const milestonesSectionMilestone = graphql`
  fragment MilestonesSectionMilestone on Issue {
    milestone {
      ...MilestonePickerMilestone
    }
  }
`

const MilestonesSectionFragment = graphql`
  fragment MilestonesSectionFragment on Issue {
    repository {
      name
      owner {
        login
      }
    }
    id
    ...MilestonesSectionMilestone
    viewerCanSetMilestone
  }
`

type EditIssueMilestonesSectionProps = {
  issue: MilestonesSectionFragment$key
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}
export function EditIssueMilestonesSection({
  issue,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: EditIssueMilestonesSectionProps) {
  const data = useFragment(MilestonesSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    id: issueId,
    viewerCanSetMilestone,
  } = data
  const {milestone} = useFragment<MilestonesSectionMilestone$key>(milestonesSectionMilestone, data)

  const activeMilestone =
    milestone !== undefined
      ? // eslint-disable-next-line no-restricted-syntax
        readInlineData<MilestonePickerMilestone$key>(MilestoneFragment, milestone)
      : null
  const sectionHeader = useMemo(() => {
    if (!viewerCanSetMilestone) {
      return <ReadonlyMilestonesSectionHeader />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlyMilestonesSectionHeader />}>
        <LazyMilestonePicker
          issueId={issueId}
          repo={repo}
          owner={owner}
          readonly={!viewerCanSetMilestone}
          shortcutEnabled={singleKeyShortcutsEnabled}
          activeMilestone={activeMilestone}
          onIssueUpdate={onIssueUpdate}
          anchorElement={MilestonesSectionHeader}
        />
      </Suspense>
    ) : (
      <LazyMilestonePickerOnClick
        issueId={issueId}
        repo={repo}
        owner={owner}
        viewerCanSetMilestone={viewerCanSetMilestone}
        singleKeyShortcutsEnabled={singleKeyShortcutsEnabled}
        activeMilestone={activeMilestone}
        onIssueUpdate={onIssueUpdate}
      />
    )
  }, [
    viewerCanSetMilestone,
    prefetchInteractionDataEnabled,
    issueId,
    repo,
    owner,
    singleKeyShortcutsEnabled,
    activeMilestone,
    onIssueUpdate,
  ])

  return <MilestonesSection sectionHeader={sectionHeader} milestone={activeMilestone} />
}

type LazyMilestonePickerOnClickProps = {
  issueId: string
  repo: string
  owner: string
  viewerCanSetMilestone: boolean
  singleKeyShortcutsEnabled: boolean
  activeMilestone: MilestonePickerMilestone$data | null
  onIssueUpdate?: () => void
}

function LazyMilestonePickerOnClick({
  issueId,
  repo,
  owner,
  viewerCanSetMilestone,
  singleKeyShortcutsEnabled,
  activeMilestone,
  onIssueUpdate,
}: LazyMilestonePickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(singleKeyShortcutsEnabled ? [HOTKEYS.milestonePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return <MilestonesSectionHeader onClick={() => setWasTriggered(true)} />
  }
  return (
    <LazyMilestonePicker
      issueId={issueId}
      repo={repo}
      owner={owner}
      readonly={!viewerCanSetMilestone}
      shortcutEnabled={singleKeyShortcutsEnabled}
      activeMilestone={activeMilestone}
      onIssueUpdate={onIssueUpdate}
      triggerOpen={true}
      anchorElement={MilestonesSectionHeader}
    />
  )
}

try{ ReadonlyMilestonesSectionHeader.displayName ||= 'ReadonlyMilestonesSectionHeader' } catch {}
try{ MilestonesSectionHeader.displayName ||= 'MilestonesSectionHeader' } catch {}
try{ MilestonesSection.displayName ||= 'MilestonesSection' } catch {}
try{ CreateIssueMilestonesSection.displayName ||= 'CreateIssueMilestonesSection' } catch {}
try{ EditIssueMilestonesSection.displayName ||= 'EditIssueMilestonesSection' } catch {}
try{ LazyMilestonePickerOnClick.displayName ||= 'LazyMilestonePickerOnClick' } catch {}